import { onBeforeUnmount } from "vue";

export type Query = Record<string, string | string[] | undefined>;

export type SavedTabQueries = Record<string, Query>;

export type VmWithRouter = {
  $router: {
    currentRoute: {
      query: Query;
    };
    replace: (args: { query: Query }) => Promise<void>;
    push: (args: { query: Query }) => Promise<void>;
  };
};

export type UseCareRecordTabQueryReturn = {
  savedTabQueries: SavedTabQueries;
  handleSelectedContentChange: (
    newSelectedContent: string,
    oldSelectedContent?: string,
  ) => Promise<void> | void;
};

export type UseCareRecordTabQueryOptions = {
  addTabAsParam?: boolean;
  stripOtherParams?: boolean;
  allowedParams?: string[];
  restoreInitialParams?: boolean;
};

export function useCareRecordTabQuery(
  vm: VmWithRouter,
  options: UseCareRecordTabQueryOptions = {},
): UseCareRecordTabQueryReturn {
  const savedTabQueries: SavedTabQueries = {};

  const {
    addTabAsParam = true,
    allowedParams = [],
    stripOtherParams = false,
    restoreInitialParams = false,
  } = options;

  function getCurrentQuery(): Query {
    return { ...vm.$router.currentRoute.query };
  }

  function getQueryWithoutCareRecordTab(query: Query): Query {
    const nextQuery = { ...query };
    delete nextQuery.careRecordTab;
    return nextQuery;
  }

  function getAllowedParamsFromQuery(query: Query): Query {
    return allowedParams.reduce<Query>((acc, key) => {
      const value = query[key];

      if (value !== undefined) {
        acc[key] = value;
      }

      return acc;
    }, {});
  }

  function saveQueryForTab(tabKey?: string): void {
    if (!tabKey) return;

    const current = getQueryWithoutCareRecordTab(getCurrentQuery());

    savedTabQueries[tabKey] = stripOtherParams ? getAllowedParamsFromQuery(current) : current;
  }

  function getSavedQueryForTab(tabKey?: string): Query {
    if (!tabKey) return {};
    return savedTabQueries[tabKey] || {};
  }

  function buildQueryForTab(tabKey?: string): Query {
    const currentQuery = getCurrentQuery();
    const allowedQueryParams = getAllowedParamsFromQuery(currentQuery);

    return {
      ...getSavedQueryForTab(tabKey),
      ...allowedQueryParams,
      ...(tabKey && addTabAsParam ? { careRecordTab: tabKey } : {}),
    };
  }

  function updateRouteForTab(tabKey?: string): Promise<void> {
    const currentQuery = getCurrentQuery();
    const nextQuery = buildQueryForTab(tabKey);

    const currentTab = currentQuery.careRecordTab ?? null;
    const nextTab = nextQuery.careRecordTab ?? null;

    const method = currentTab !== nextTab ? "push" : "replace";

    return vm.$router[method]({
      query: nextQuery,
    });
  }

  function handleSelectedContentChange(
    newSelectedContent: string,
    oldSelectedContent?: string,
  ): Promise<void> | void {
    saveQueryForTab(oldSelectedContent);
    return updateRouteForTab(newSelectedContent);
  }

  const initialQuerySnapshot = getCurrentQuery();

  function buildRestoredQuery(): Query {
    return { ...initialQuerySnapshot };
  }

  onBeforeUnmount(() => {
    if (!restoreInitialParams) return;

    const currentQuery = getCurrentQuery();
    const restoredQuery = buildRestoredQuery();
    const currentKeys = Object.keys(currentQuery);
    const restoredKeys = Object.keys(restoredQuery);
    const isSameLength = currentKeys.length === restoredKeys.length;
    const isSameQuery =
      isSameLength &&
      currentKeys.every((key) => {
        const currentValue = currentQuery[key];
        const restoredValue = restoredQuery[key];

        if (Array.isArray(currentValue) || Array.isArray(restoredValue)) {
          return JSON.stringify(currentValue) === JSON.stringify(restoredValue);
        }

        return currentValue === restoredValue;
      });
    if (isSameQuery) return;

    void vm.$router.replace({
      query: restoredQuery,
    });
  });

  return {
    savedTabQueries,
    handleSelectedContentChange,
  };
}
